(() => {
  class ScAnnouncement extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      this.slides = [...this.querySelectorAll('.sc-announcement__message')];
      this.button = this.querySelector('button');
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.index = 0;
      this.paused = false;
      const options = { signal: this.controller.signal };
      this.button?.addEventListener('click', () => {
        this.paused = !this.paused;
        this.button.setAttribute('aria-pressed', String(this.paused));
        this.button.setAttribute('aria-label', this.paused ? this.button.dataset.playLabel : this.button.dataset.pauseLabel);
        this.button.innerHTML = this.paused
          ? '<svg viewBox="0 0 16 16" width="14" height="14" fill="currentColor" aria-hidden="true"><path d="m5 2 9 6-9 6z"/></svg>'
          : '<svg viewBox="0 0 16 16" width="14" height="14" fill="currentColor" aria-hidden="true"><path d="M4 3h3v10H4zm5 0h3v10H9z"/></svg>';
      }, options);
      this.motion.addEventListener('change', () => this.configure(), options);
      this.addEventListener('shopify:block:select', (event) => {
        const index = this.slides.findIndex((slide) => slide.contains(event.target));
        if (index < 0) return;
        this.index = index;
        this.paused = true;
        this.show();
      }, options);
      this.configure();
    }

    configure() {
      clearInterval(this.timer);
      const rotating = this.dataset.rotate === 'true' && !this.motion.matches && this.slides.length > 1;
      this.toggleAttribute('data-enhanced', rotating);
      this.slides.forEach((slide) => { slide.hidden = false; });
      if (!rotating) return;
      this.show();
      this.timer = setInterval(() => {
        if (this.paused || document.hidden || this.matches(':hover') || this.contains(document.activeElement)) return;
        this.index = (this.index + 1) % this.slides.length;
        this.show();
      }, Number(this.dataset.speed) || 5000);
    }

    show() {
      if (this.motion.matches) return;
      this.slides.forEach((slide, index) => { slide.hidden = index !== this.index; });
    }

    disconnectedCallback() {
      clearInterval(this.timer);
      this.controller.abort();
    }
  }

  class StickyHeader extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.section-header');
      if (!this.section) return;
      this.controller = new AbortController();
      this.lastScroll = window.scrollY;
      this.threshold = this.section.getBoundingClientRect().top + window.scrollY;
      const updateSize = () => document.documentElement.style.setProperty('--header-height', `${this.section.offsetHeight}px`);
      this.resizeObserver = new ResizeObserver(updateSize);
      this.resizeObserver.observe(this.section);
      updateSize();
      const options = { signal: this.controller.signal };
      window.addEventListener('scroll', () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          const scroll = Math.max(0, window.scrollY);
          const delta = scroll - this.lastScroll;
          const locked = this.querySelector('header-drawer > details[open], details-modal > details[open]') || this.contains(document.activeElement);
          this.section.classList.toggle('scrolled-past-header', scroll > this.threshold);
          const mode = this.dataset.stickyType;
          if (locked || scroll <= this.threshold + this.section.offsetHeight || mode === 'always' || mode === 'reduce-logo-size') {
            this.section.classList.remove('shopify-section-header-hidden');
          } else if (Math.abs(delta) > 3 && mode !== 'none') {
            this.section.classList.toggle('shopify-section-header-hidden', delta > 0);
          }
          if (Math.abs(delta) > 3) this.lastScroll = scroll;
        });
      }, { ...options, passive: true });
      if (this.dataset.stickyType === 'none') this.section.style.position = 'relative';
      this.addEventListener('focusin', () => this.section.classList.remove('shopify-section-header-hidden'), options);
      this.initializeMenus(options);
      customElements.whenDefined('header-drawer').then(() => {
        if (this.isConnected) this.initializeDrawer(options);
      });
      this.initializeSearch(options);
      this.initializeCart();
    }

    initializeMenus(options) {
      const menus = [...this.querySelectorAll('header-menu')];
      menus.forEach((menu) => {
        const details = menu.querySelector('details');
        const summary = details.querySelector('summary');
        let timer;
        const open = () => {
          menus.forEach((other) => {
            if (other !== menu) other.querySelector('details').open = false;
          });
          details.open = true;
        };
        const close = () => { details.open = false; };
        menu.addEventListener('pointerenter', (event) => {
          if (event.pointerType !== 'mouse' || !matchMedia('(min-width: 990px)').matches) return;
          clearTimeout(timer);
          timer = setTimeout(open, 140);
        }, options);
        menu.addEventListener('pointerleave', (event) => {
          if (event.pointerType !== 'mouse') return;
          clearTimeout(timer);
          if (!menu.contains(document.activeElement)) timer = setTimeout(close, 180);
        }, options);
        summary.addEventListener('click', () => {
          clearTimeout(timer);
          if (!details.open) menus.forEach((other) => {
            if (other !== menu) other.querySelector('details').open = false;
          });
        }, options);
        details.addEventListener('toggle', () => summary.setAttribute('aria-expanded', String(details.open)), options);
        menu.addEventListener('keydown', (event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            close();
            summary.focus();
          }
          if (event.target === summary && event.key === 'ArrowDown') {
            event.preventDefault();
            open();
            details.querySelector('a')?.focus();
          }
        }, options);
        this.controller.signal.addEventListener('abort', () => clearTimeout(timer), { once: true });
      });
      document.addEventListener('pointerdown', (event) => {
        menus.forEach((menu) => {
          if (!menu.contains(event.target)) menu.querySelector('details').open = false;
        });
      }, options);
    }

    initializeDrawer(options) {
      const drawer = this.querySelector('header-drawer');
      if (!drawer) return;
      const root = drawer.querySelector('details');
      const trigger = root.querySelector('summary');
      const panel = drawer.querySelector('#menu-drawer');
      const reduce = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
      const oldOpen = drawer.openMenuDrawer.bind(drawer);
      const oldClose = drawer.closeMenuDrawer.bind(drawer);
      drawer.openMenuDrawer = (summary) => {
        this.querySelector('details-modal')?.close(false);
        oldOpen(summary);
        this.drawerFocusTimer = setTimeout(() => {
          if (root.open) trapFocus(panel, panel.querySelector('[data-sc-drawer-close]'));
        }, 0);
      };
      drawer.closeMenuDrawer = (event, focus = trigger) => {
        clearTimeout(this.drawerFocusTimer);
        oldClose(event || new Event('close'), focus);
        trigger.setAttribute('aria-expanded', 'false');
        root.querySelectorAll('summary').forEach((summary) => summary.setAttribute('aria-expanded', 'false'));
      };
      drawer.closeAnimation = (details) => {
        const finish = () => {
          if (details.classList.contains('menu-opening')) return;
          details.open = false;
          const parent = details.parentElement.closest('details[open]');
          if (parent) trapFocus(parent === root ? panel : parent.querySelector('.menu-drawer__submenu'), details.querySelector('summary'));
        };
        setTimeout(finish, reduce() ? 0 : 600);
      };
      drawer.addEventListener('click', (event) => {
        if (event.target.closest('[data-sc-drawer-close], [data-sc-drawer-overlay]')) {
          event.preventDefault();
          event.stopImmediatePropagation();
          drawer.closeMenuDrawer(event, trigger);
          return;
        }
        const summary = event.target.closest('summary');
        if (summary && summary.parentElement !== root && summary.parentElement.open) {
          event.preventDefault();
          event.stopImmediatePropagation();
          drawer.closeSubmenu(summary.parentElement);
        }
      }, { ...options, capture: true });
      drawer.addEventListener('keydown', (event) => {
        if (event.key !== 'Tab' || !root.open) return;
        const openSubmenus = [...panel.querySelectorAll('details[open]')];
        const activePanel = openSubmenus.length
          ? openSubmenus[openSubmenus.length - 1].querySelector('.menu-drawer__submenu')
          : panel;
        const dismiss = panel.querySelector('[data-sc-drawer-close]');
        const candidates = [...new Set([dismiss, ...getFocusableElements(activePanel)])].filter((element) =>
          element && element.getClientRects().length && getComputedStyle(element).visibility === 'visible'
        );
        if (!candidates.length) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        const index = candidates.indexOf(document.activeElement);
        const next = (index + (event.shiftKey ? -1 : 1) + candidates.length) % candidates.length;
        candidates[next].focus();
      }, { ...options, capture: true });
      const desktop = matchMedia('(min-width: 990px)');
      desktop.addEventListener('change', () => {
        if (desktop.matches && root.open) drawer.closeMenuDrawer(new Event('resize'), trigger);
      }, options);
      this.controller.signal.addEventListener('abort', () => {
        clearTimeout(this.drawerFocusTimer);
        if (root.open) drawer.closeMenuDrawer(new Event('disconnect'), trigger);
      }, { once: true });
    }

    initializeCart() {
      const icon = this.querySelector('#cart-icon-bubble');
      if (!icon) return;
      const updateLabel = () => {
        const label = [...icon.querySelectorAll('.visually-hidden')]
          .map((element) => element.textContent.trim()).filter(Boolean).join(': ');
        if (label) icon.setAttribute('aria-label', label);
      };
      updateLabel();
      this.cartObserver = new MutationObserver(updateLabel);
      this.cartObserver.observe(icon, { childList: true, subtree: true, characterData: true });
    }

    initializeSearch(options) {
      const modal = this.querySelector('details-modal');
      if (!modal) return;
      const details = modal.querySelector('details');
      const summary = modal.querySelector('summary');
      details.addEventListener('toggle', () => {
        summary.setAttribute('aria-expanded', String(details.open));
        if (!details.open) modal.querySelector('predictive-search')?.close();
      }, options);
      summary.addEventListener('click', () => {
        const drawer = this.querySelector('header-drawer');
        if (drawer?.querySelector('details').open) drawer.closeMenuDrawer(new Event('close'), drawer.querySelector('summary'));
      }, { ...options, capture: true });
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.resizeObserver?.disconnect();
      this.cartObserver?.disconnect();
      cancelAnimationFrame(this.frame);
      this.querySelector('details-modal')?.close(false);
    }
  }

  if (!customElements.get('sc-announcement')) customElements.define('sc-announcement', ScAnnouncement);
  if (!customElements.get('sticky-header')) customElements.define('sticky-header', StickyHeader);

  const initializeFooter = (scope) => {
    scope.querySelectorAll('.sc-footer__links:not([data-sc-ready])').forEach((details) => {
      details.dataset.scReady = 'true';
      const desktop = matchMedia('(min-width: 990px)');
      const summary = details.querySelector('summary');
      const update = () => {
        details.open = desktop.matches;
        summary.tabIndex = desktop.matches ? -1 : 0;
      };
      update();
      desktop.addEventListener('change', update);
      summary.addEventListener('click', (event) => { if (desktop.matches) event.preventDefault(); });
      document.addEventListener('shopify:section:unload', (event) => {
        if (event.target.contains(details)) desktop.removeEventListener('change', update);
      }, { once: true });
    });
  };
  initializeFooter(document);
  document.addEventListener('shopify:section:load', (event) => initializeFooter(event.target));
})();
